function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }
  
  connectedCallback() {
    // Wait for element to be fully in DOM before binding events
    this.bindEvents();
  }

  bindEvents() {
    // Handle main toggle (for opening/closing the drawer)
    const mainSummary = this.mainDetailsToggle?.querySelector('summary');
    if (mainSummary) {
      mainSummary.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = this.mainDetailsToggle.hasAttribute('open');
        if (isOpen) {
          this.closeMenuDrawer(event, mainSummary);
        } else {
          this.openMenuDrawer(mainSummary);
        }
      });
    }
    
    // Handle menu item summaries (like SHOP) for submenus
    // Use event delegation on the drawer - this is the most reliable method
    const drawer = this.querySelector('.menu-drawer') || this.querySelector('#menu-drawer');
    console.log('🎯 bindEvents: drawer found?', !!drawer);
    console.log('🎯 Drawer element:', drawer);
    console.log('🎯 Drawer ID:', drawer?.id);
    console.log('🎯 Drawer classes:', drawer?.className);
    
    if (drawer) {
      // Handle clicks on menu item summaries (like SHOP) - use document-level listener
      // because the native <summary> click might not bubble properly
      const handleMenuItemClick = (event) => {
        // Only handle clicks inside the drawer
        if (!event.target.closest('.menu-drawer')) {
          return;
        }
        
        // Skip if clicking on close button (X button)
        if (event.target.closest('.menu-drawer__close-button-top')) {
          return;
        }
        
        // Handle back button (← SHOP) click - close the submenu
        const backButton = event.target.closest('.menu-drawer__close-button');
        if (backButton && !backButton.classList.contains('menu-drawer__close-button-top')) {
          // CRITICAL: Stop all event propagation immediately to prevent other handlers
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Mark this as a submenu back button click to prevent handleDocumentClick from closing main drawer
          event._isSubmenuBackButton = true;
          
          console.log('🎯 Back button clicked - closing submenu');
          
          // Find the submenu that contains the back button
          const submenu = backButton.closest('.menu-drawer__submenu');
          if (submenu) {
            console.log('🎯 Submenu found, looking for details element');
            console.log('🎯 Submenu _originalParent:', submenu._originalParent);
            console.log('🎯 Submenu _originalDetails:', submenu._originalDetails);
            console.log('🎯 _originalParent tagName:', submenu._originalParent?.tagName);
            console.log('🎯 _originalParent has open?', submenu._originalParent?.hasAttribute('open'));
            
            // Find the details element - try multiple methods
            let openDetails = null;
            
            // Method 1: If submenu was moved, use the stored original details element
            // Check _originalDetails first, then _originalParent
            if (submenu._originalDetails) {
              if (submenu._originalDetails.tagName === 'DETAILS') {
                openDetails = submenu._originalDetails;
                console.log('🎯 Method 1a: Using _originalDetails:', openDetails.id);
              } else {
                console.log('⚠️ _originalDetails is not a DETAILS element:', submenu._originalDetails.tagName);
              }
            }
            
            if (!openDetails && submenu._originalParent) {
              // Check if _originalParent IS the details element
              if (submenu._originalParent.tagName === 'DETAILS') {
                openDetails = submenu._originalParent;
                console.log('🎯 Method 1b: Using _originalParent (is details):', openDetails.id);
              } else {
                // Walk up from original parent to find details
                openDetails = submenu._originalParent;
                while (openDetails && openDetails.tagName !== 'DETAILS') {
                  openDetails = openDetails.parentElement;
                }
                if (openDetails) {
                  console.log('🎯 Method 1c: Found details by walking up from _originalParent:', openDetails.id);
                }
              }
            }
            
            // Don't verify open attribute here - we'll check it in the final if statement
            
            // Method 2: Search the drawer for any open details (not the main toggle)
            if (!openDetails || openDetails === this.mainDetailsToggle) {
              const drawer = submenu.closest('.menu-drawer') || this.querySelector('.menu-drawer');
              if (drawer) {
                // Find all open details and get the one that's not the main toggle
                const allOpenDetails = drawer.querySelectorAll('details[open]');
                console.log('🎯 Found open details in drawer:', allOpenDetails.length);
                for (let details of allOpenDetails) {
                  console.log('🎯 Checking details:', details.id, 'is main toggle?', details === this.mainDetailsToggle);
                  if (details !== this.mainDetailsToggle) {
                    openDetails = details;
                    console.log('🎯 Selected details:', details.id);
                    break;
                  }
                }
              }
            }
            
            // Method 3: Search in the navigation for open details
            if (!openDetails || openDetails === this.mainDetailsToggle) {
              const navigation = this.querySelector('.menu-drawer__navigation');
              if (navigation) {
                const navOpenDetails = navigation.querySelectorAll('details[open]');
                console.log('🎯 Found open details in navigation:', navOpenDetails.length);
                for (let details of navOpenDetails) {
                  if (details !== this.mainDetailsToggle) {
                    openDetails = details;
                    console.log('🎯 Selected details from navigation:', details.id);
                    break;
                  }
                }
              }
            }
            
            // Method 4: Search by ID if we know the submenu ID
            if (!openDetails || openDetails === this.mainDetailsToggle) {
              const submenuId = submenu.id;
              if (submenuId) {
                // Submenu ID is like "link-shop", details ID should be "Details-menu-drawer-menu-item-1"
                // Try to find details that contains a summary with matching handle
                const drawer = submenu.closest('.menu-drawer') || this.querySelector('.menu-drawer');
                if (drawer) {
                  const allDetails = drawer.querySelectorAll('details');
                  for (let details of allDetails) {
                    const summary = details.querySelector('summary');
                    if (summary && summary.id && summary.id.includes('shop')) {
                      if (details.hasAttribute('open')) {
                        openDetails = details;
                        console.log('🎯 Found details by summary ID:', details.id);
                        break;
                      }
                    }
                  }
                }
              }
            }
            
            // Final check - make sure we have a valid details element
            if (openDetails) {
              console.log('🎯 openDetails found:', openDetails.id);
              console.log('🎯 openDetails tagName:', openDetails.tagName);
              console.log('🎯 openDetails === mainDetailsToggle?', openDetails === this.mainDetailsToggle);
              console.log('🎯 openDetails hasAttribute("open")?', openDetails.hasAttribute('open'));
              console.log('🎯 openDetails has class "menu-opening"?', openDetails.classList.contains('menu-opening'));
              console.log('🎯 openDetails has class "has-open-submenu"?', openDetails.classList.contains('has-open-submenu'));
            }
            
            // Check if details is open - either by attribute OR by class (since attribute might be removed)
            const isDetailsOpen = openDetails && 
              openDetails !== this.mainDetailsToggle && 
              (openDetails.hasAttribute('open') || 
               openDetails.classList.contains('menu-opening') || 
               openDetails.classList.contains('has-open-submenu'));
            
            if (isDetailsOpen) {
              console.log('✅✅✅ Found open details, closing it:', openDetails.id);
              
              // CRITICAL: Set flag IMMEDIATELY to prevent main drawer from closing
              // This must happen BEFORE any DOM manipulation
              document._preventingMainDrawerClose = true;
              setTimeout(() => {
                document._preventingMainDrawerClose = false;
                console.log('🎯 _preventingMainDrawerClose flag cleared');
              }, 2000);
              
              // CRITICAL: Ensure main drawer stays open BEFORE closing submenu
              // This prevents any events from closing it
              if (!this.mainDetailsToggle.hasAttribute('open')) {
                console.log('⚠️ Main drawer missing open attribute, adding it BEFORE closing submenu');
                this.mainDetailsToggle.setAttribute('open', '');
              }
              if (!this.mainDetailsToggle.classList.contains('menu-opening')) {
                console.log('⚠️ Main drawer missing menu-opening class, adding it BEFORE closing submenu');
                this.mainDetailsToggle.classList.add('menu-opening');
              }
              
              // Find the summary element
              const summary = openDetails.querySelector('summary.menu-drawer__menu-item');
              
              // Close the submenu by removing open attribute
              // Use a MutationObserver to watch for any changes to main drawer
              const mainDrawerObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                    if (!this.mainDetailsToggle.hasAttribute('open') && document._preventingMainDrawerClose) {
                      console.log('🚨🚨🚨 Main drawer open attribute was removed! Restoring it immediately!');
                      this.mainDetailsToggle.setAttribute('open', '');
                      this.mainDetailsToggle.classList.add('menu-opening');
                    }
                  }
                  if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    if (!this.mainDetailsToggle.classList.contains('menu-opening') && document._preventingMainDrawerClose) {
                      console.log('🚨🚨🚨 Main drawer menu-opening class was removed! Restoring it immediately!');
                      this.mainDetailsToggle.classList.add('menu-opening');
                    }
                  }
                });
              });
              
              // Observe the main drawer for attribute changes
              mainDrawerObserver.observe(this.mainDetailsToggle, {
                attributes: true,
                attributeFilter: ['open', 'class']
              });
              
              // Store observer so we can disconnect it later
              this._mainDrawerObserver = mainDrawerObserver;
              
              // Now close the submenu
              openDetails.removeAttribute('open');
              openDetails.classList.remove('menu-opening');
              openDetails.classList.remove('has-open-submenu');
              
              // Remove submenu classes
              submenu.classList.remove('submenu-is-open');
              
              // Reset summary aria
              if (summary) {
                summary.setAttribute('aria-expanded', 'false');
              }
              
              // Verify main drawer is still open after closing submenu
              if (!this.mainDetailsToggle.hasAttribute('open')) {
                console.log('⚠️ Main drawer lost open attribute after closing submenu, restoring it');
                this.mainDetailsToggle.setAttribute('open', '');
              }
              if (!this.mainDetailsToggle.classList.contains('menu-opening')) {
                console.log('⚠️ Main drawer lost menu-opening class after closing submenu, restoring it');
                this.mainDetailsToggle.classList.add('menu-opening');
              }
              
              // Restore submenu to original parent if it was moved
              // Do this AFTER ensuring main drawer is open
              if (submenu._originalParent && submenu.parentElement !== submenu._originalParent) {
                submenu._originalParent.appendChild(submenu);
                delete submenu._originalParent;
                delete submenu._originalDetails;
              }
              
              // Remove submenu classes AFTER restoring
              submenu.classList.remove('submenu-is-open');
              
              // Mark that we just closed a submenu to prevent onFocusOut from closing main drawer
              document._justClosedSubmenu = true;
              // Keep flag active longer to prevent any delayed focus handlers
          setTimeout(() => {
                document._justClosedSubmenu = false;
                console.log('🎯 _justClosedSubmenu flag cleared');
              }, 2000); // Keep flag for 2 seconds to prevent delayed focus handlers
              
              // CRITICAL: Focus the main drawer summary to prevent focus loss
              const mainSummary = this.mainDetailsToggle.querySelector('summary');
              if (mainSummary) {
                // Use setTimeout to focus after DOM updates
                setTimeout(() => {
                  try {
                    mainSummary.focus();
                    console.log('🎯 Focused main summary to prevent focus loss');
                  } catch (e) {
                    console.log('🎯 Could not focus main summary:', e);
                  }
                }, 50);
              }
              
              console.log('✅ Submenu closed, main drawer should still be open');
              console.log('✅ Main drawer open attribute:', this.mainDetailsToggle.hasAttribute('open'));
              console.log('✅ Main drawer menu-opening class:', this.mainDetailsToggle.classList.contains('menu-opening'));
              
              // CRITICAL: Ensure main drawer stays open - check multiple times
              const ensureMainDrawerOpen = () => {
                if (!this.mainDetailsToggle.hasAttribute('open')) {
                  console.log('⚠️ Main drawer was closed, reopening it');
                  this.mainDetailsToggle.setAttribute('open', '');
                  this.mainDetailsToggle.classList.add('menu-opening');
                }
                if (!this.mainDetailsToggle.classList.contains('menu-opening')) {
                  console.log('⚠️ Main drawer missing menu-opening, adding it');
                  this.mainDetailsToggle.classList.add('menu-opening');
                }
              };
              
              // Check immediately
              ensureMainDrawerOpen();
              
              // Check after requestAnimationFrame (runs after current frame)
              requestAnimationFrame(ensureMainDrawerOpen);
              
              // Check after short delays to catch any setTimeout handlers
              setTimeout(ensureMainDrawerOpen, 10);
              setTimeout(ensureMainDrawerOpen, 50);
              setTimeout(ensureMainDrawerOpen, 100);
              setTimeout(ensureMainDrawerOpen, 200);
              setTimeout(ensureMainDrawerOpen, 500);
              setTimeout(ensureMainDrawerOpen, 1000);
              
              // CRITICAL: Return false to prevent any further event handling
          return false;
            } else {
              console.log('❌ Could not find open details element');
              console.log('❌ openDetails:', openDetails);
              console.log('❌ openDetails === mainDetailsToggle?', openDetails === this.mainDetailsToggle);
              console.log('❌ openDetails hasAttribute("open")?', openDetails?.hasAttribute('open'));
              console.log('❌ Searched submenu:', submenu);
              console.log('❌ _originalParent:', submenu._originalParent);
              console.log('❌ _originalDetails:', submenu._originalDetails);
            }
          }
        }
        
        console.log('🔥🔥🔥 DOCUMENT CLICK - Inside drawer!', event.target);
        
        // Find the summary element that was clicked
        let clickedSummary = event.target.closest('summary.menu-drawer__menu-item');
        
        // If not found, check if target IS a summary
        if (!clickedSummary && event.target.tagName === 'SUMMARY' && event.target.classList.contains('menu-drawer__menu-item')) {
          clickedSummary = event.target;
        }
        
        // If still not found, walk up the parent chain
        if (!clickedSummary) {
          let element = event.target;
          while (element && element !== drawer) {
            if (element.tagName === 'SUMMARY' && element.classList.contains('menu-drawer__menu-item')) {
              clickedSummary = element;
              break;
            }
            element = element.parentElement;
          }
        }
        
        if (!clickedSummary) {
          return; // Not a menu item click
        }
        
        console.log('🎯✅✅✅ SHOP CLICKED!', clickedSummary.textContent.trim());
        
        // Get the details element (parent of summary)
        const detailsElement = clickedSummary.parentElement;
        
        if (!detailsElement || detailsElement.tagName !== 'DETAILS') {
          console.error('❌ Invalid details element');
          return;
        }
        
        console.log('🎯 Details element:', detailsElement, 'tagName:', detailsElement.tagName);
        
        // Toggle details open
        const wasOpen = detailsElement.hasAttribute('open');
        console.log('🎯 Details was open?', wasOpen);
        
        if (wasOpen) {
          // Close it
          const submenu = detailsElement.querySelector('.menu-drawer__submenu');
          
          // Restore submenu to original parent if it was moved
          if (submenu && submenu._originalParent && submenu.parentElement !== submenu._originalParent) {
            submenu._originalParent.appendChild(submenu);
            delete submenu._originalParent;
          }
          
          // Reset submenu styles
          if (submenu) {
            const innerSubmenu = submenu.querySelector('.menu-drawer__inner-submenu');
            if (innerSubmenu) {
              innerSubmenu.style.removeProperty('display');
              innerSubmenu.style.removeProperty('visibility');
              innerSubmenu.style.removeProperty('opacity');
            }
            const menuItems = submenu.querySelectorAll('.menu-drawer__menu li, .menu-drawer__menu a');
            menuItems.forEach(item => {
              item.style.removeProperty('display');
              item.style.removeProperty('visibility');
              item.style.removeProperty('opacity');
            });
          }
          
          detailsElement.removeAttribute('open');
          detailsElement.classList.remove('menu-opening');
          clickedSummary.setAttribute('aria-expanded', 'false');
          console.log('🎯 Closed details');
        } else {
          // Open it
          detailsElement.setAttribute('open', '');
          detailsElement.classList.add('menu-opening');
          clickedSummary.setAttribute('aria-expanded', 'true');
          console.log('🎯 Opened details, has open attr?', detailsElement.hasAttribute('open'));
          
          // Find and show submenu
          let submenu = clickedSummary.nextElementSibling;
          if (!submenu || !submenu.classList.contains('menu-drawer__submenu')) {
            submenu = detailsElement.querySelector('.menu-drawer__submenu');
          }
          
          console.log('🎯 Submenu found?', !!submenu);
          console.log('🎯 Details children:', Array.from(detailsElement.children).map(c => ({ tag: c.tagName, class: c.className, id: c.id })));
          
          if (submenu) {
            console.log('✅✅✅ SUBMENU FOUND! Applying styles...');
            
            // Add a class to mark submenu as open - this helps CSS target it
            submenu.classList.add('submenu-is-open');
            detailsElement.classList.add('has-open-submenu');
            
            // Ensure summary stays visible
            clickedSummary.style.setProperty('display', 'block', 'important');
            clickedSummary.style.setProperty('visibility', 'visible', 'important');
            clickedSummary.style.setProperty('opacity', '1', 'important');
            clickedSummary.style.setProperty('position', 'relative', 'important');
            clickedSummary.style.setProperty('z-index', '11', 'important');
            
            // CRITICAL: Move submenu to drawer container so fixed positioning works
            // The submenu is currently inside <details>, which creates a positioning context
            // Moving it to the drawer container allows true fixed positioning
            const drawer = submenu.closest('.menu-drawer');
            const drawerInner = drawer?.querySelector('.menu-drawer__inner-container') || drawer;
            
            if (drawerInner && submenu.parentElement !== drawerInner) {
              console.log('🎯 Moving submenu to drawer container for proper positioning');
              // Store the details element directly - we already have it!
              submenu._originalParent = submenu.parentElement;
              submenu._originalDetails = detailsElement; // Use the detailsElement we already have
              console.log('🎯 Stored original parent:', submenu._originalParent);
              console.log('🎯 Stored original details:', submenu._originalDetails);
              console.log('🎯 Details element ID:', detailsElement.id);
              // Move to drawer container
              drawerInner.appendChild(submenu);
            } else {
              // Even if not moved, store the details reference for back button
              submenu._originalDetails = detailsElement;
              console.log('🎯 Stored details reference (not moved):', detailsElement.id);
            }
            
            // Remove transition temporarily to prevent animation interference
            submenu.style.setProperty('transition', 'none', 'important');
            
            if (drawerInner) {
              const drawerRect = drawerInner.getBoundingClientRect();
              console.log('🎯 Drawer rect:', drawerRect);
              
              // Use fixed positioning relative to viewport, positioned to match drawer
              submenu.style.setProperty('position', 'fixed', 'important');
              submenu.style.setProperty('top', `${drawerRect.top}px`, 'important');
              submenu.style.setProperty('left', `${drawerRect.left}px`, 'important');
              submenu.style.setProperty('width', `${drawerRect.width}px`, 'important');
              submenu.style.setProperty('height', `${drawerRect.height}px`, 'important');
              submenu.style.setProperty('right', 'auto', 'important');
              submenu.style.setProperty('bottom', 'auto', 'important');
            }
            
            // Apply all styles to make submenu visible
            submenu.style.setProperty('display', 'block', 'important');
            submenu.style.setProperty('visibility', 'visible', 'important');
            submenu.style.setProperty('opacity', '1', 'important');
            submenu.style.setProperty('pointer-events', 'auto', 'important');
            submenu.style.setProperty('transform', 'translateX(0)', 'important');
            submenu.style.setProperty('z-index', '1000', 'important'); // Lower than X button (1001)
            submenu.style.setProperty('background-color', '#ffffff', 'important');
            submenu.style.setProperty('overflow-y', 'auto', 'important');
            submenu.style.setProperty('overflow-x', 'hidden', 'important');
            
            // CRITICAL: Ensure inner content is also visible
            const innerSubmenu = submenu.querySelector('.menu-drawer__inner-submenu');
            if (innerSubmenu) {
              innerSubmenu.style.setProperty('display', 'block', 'important');
              innerSubmenu.style.setProperty('visibility', 'visible', 'important');
              innerSubmenu.style.setProperty('opacity', '1', 'important');
            }
            
            // Ensure all menu items inside are visible
            const menuItems = submenu.querySelectorAll('.menu-drawer__menu li, .menu-drawer__menu a');
            menuItems.forEach(item => {
              item.style.setProperty('display', 'block', 'important');
              item.style.setProperty('visibility', 'visible', 'important');
              item.style.setProperty('opacity', '1', 'important');
            });
            
            // Force a reflow to ensure styles apply
            void submenu.offsetHeight;
            
            // Apply again after reflow to ensure they stick
            requestAnimationFrame(() => {
              submenu.style.setProperty('transform', 'translateX(0)', 'important');
              submenu.style.setProperty('visibility', 'visible', 'important');
              submenu.style.setProperty('opacity', '1', 'important');
              submenu.style.setProperty('transition', 'none', 'important');
              
              // Verify they're applied
              const computed = window.getComputedStyle(submenu);
              console.log('🎯 After RAF - transform:', computed.transform);
              console.log('🎯 After RAF - visibility:', computed.visibility);
              console.log('🎯 After RAF - opacity:', computed.opacity);
            });
            
            console.log('✅ Styles applied to submenu');
            
            // Verify styles were applied and check positioning
            setTimeout(() => {
              const computed = window.getComputedStyle(submenu);
              const submenuRect = submenu.getBoundingClientRect();
              console.log('🎯 Submenu computed styles:');
              console.log('  - display:', computed.display);
              console.log('  - visibility:', computed.visibility);
              console.log('  - opacity:', computed.opacity);
              console.log('  - transform:', computed.transform);
              console.log('  - position:', computed.position);
              console.log('  - z-index:', computed.zIndex);
              console.log('  - top:', computed.top);
              console.log('  - left:', computed.left);
              console.log('  - width:', computed.width);
              console.log('  - height:', computed.height);
              console.log('🎯 Submenu bounding rect:', submenuRect);
              console.log('🎯 Submenu has content?', submenu.children.length, 'children');
              console.log('🎯 Submenu innerHTML length:', submenu.innerHTML.length);
              console.log('🎯 Submenu is in viewport?', 
                submenuRect.top >= 0 && 
                submenuRect.left >= 0 && 
                submenuRect.bottom <= window.innerHeight && 
                submenuRect.right <= window.innerWidth
              );
              
              // Check inner content visibility
              const innerSubmenu = submenu.querySelector('.menu-drawer__inner-submenu');
              if (innerSubmenu) {
                const innerComputed = window.getComputedStyle(innerSubmenu);
                const innerRect = innerSubmenu.getBoundingClientRect();
                console.log('🎯 Inner submenu computed:');
                console.log('  - display:', innerComputed.display);
                console.log('  - visibility:', innerComputed.visibility);
                console.log('  - opacity:', innerComputed.opacity);
                console.log('  - bounding rect:', innerRect);
              }
              
              // Check if submenu is actually visible in DOM
              console.log('🎯 Submenu offsetParent:', submenu.offsetParent);
              console.log('🎯 Submenu parentElement:', submenu.parentElement);
              console.log('🎯 Submenu isConnected:', submenu.isConnected);
            }, 50);
          } else {
            console.error('❌❌❌ SUBMENU NOT FOUND!');
            console.error('Details element:', detailsElement);
            console.error('Details children count:', detailsElement.children.length);
            console.error('Details HTML:', detailsElement.innerHTML.substring(0, 500));
          }
        }
      };
      
      // Use capture phase to catch it before native handlers
      document.addEventListener('click', handleMenuItemClick, true);
      
      console.log('✅ Click handler attached to drawer');
    } else {
      console.error('❌ Drawer not found in bindEvents!');
    }
    
    // REMOVED: Direct event listeners that were preventing natural details open behavior
    // The event delegation handler above handles this correctly by letting details open naturally
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.menu-drawer__close-button-top)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    console.log('🎯 onSummaryClick called');
    // This function is ONLY called for menu items (like SHOP), not the main toggle
    // Use the correct summary from the event (set in bindEvents) or fall back to currentTarget
    const summaryElement = event._correctSummary || event.currentTarget;
    
    // The details element MUST be the immediate parent of the summary
    // In HTML, <details><summary>...</summary></details> structure
    const detailsElement = summaryElement.parentElement;
    
    console.log('🎯 Summary element:', summaryElement);
    console.log('🎯 Details element:', detailsElement);
    console.log('🎯 Details has open?', detailsElement.hasAttribute('open'));
    
    // Validate we have the right elements
    if (!summaryElement || summaryElement.tagName !== 'SUMMARY') {
      console.error('❌ Invalid summary element:', summaryElement);
      return;
    }
    
    if (!detailsElement || detailsElement.tagName !== 'DETAILS') {
      console.error('❌ Invalid details element:', detailsElement);
      return;
    }
    
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // This function is ONLY called for menu items, so we know it's a menu item
    const isMenuItem = true;
    
    // Handle menu item submenu
    // DON'T preventDefault - we need details to open naturally for CSS to work
    // The event delegation handler already stopped propagation to prevent duplicate glitch
    event.stopPropagation();
    
    // Find the submenu - it should be the nextElementSibling of the summary
    // OR we can query for it within the details element
    let submenu = summaryElement.nextElementSibling;
    console.log('🎯 Submenu from nextElementSibling:', submenu);
    
    // If nextElementSibling doesn't have the class, try querySelector
    if (!submenu || !submenu.classList.contains('menu-drawer__submenu')) {
      submenu = detailsElement.querySelector('.menu-drawer__submenu');
      console.log('🎯 Submenu from querySelector:', submenu);
    }
    
    if (!submenu) {
      console.error('❌ NO SUBMENU FOUND!');
      console.error('Details children:', Array.from(detailsElement.children));
      return false;
    }
    
    if (!submenu.classList.contains('menu-drawer__submenu')) {
      console.error('❌ Found element is not a submenu:', submenu);
      return false;
    }
    
    console.log('✅ Submenu found:', submenu);
    
    // Details should already be open from natural click behavior (since we didn't preventDefault)
    // But ensure it's open in case it wasn't
    if (!detailsElement.hasAttribute('open')) {
      detailsElement.setAttribute('open', '');
    }
    
    
    // CRITICAL: Keep summary visible when details opens - SET IMMEDIATELY
    // Don't wait for requestAnimationFrame - set it right away
    summaryElement.style.setProperty('display', 'block', 'important');
    summaryElement.style.setProperty('visibility', 'visible', 'important');
    summaryElement.style.setProperty('opacity', '1', 'important');
    summaryElement.style.setProperty('position', 'relative', 'important');
    summaryElement.style.setProperty('z-index', '11', 'important');
    
    // Also set in requestAnimationFrame as backup
    requestAnimationFrame(() => {
      summaryElement.style.setProperty('display', 'block', 'important');
      summaryElement.style.setProperty('visibility', 'visible', 'important');
      summaryElement.style.setProperty('opacity', '1', 'important');
      summaryElement.style.setProperty('position', 'relative', 'important');
      summaryElement.style.setProperty('z-index', '11', 'important');
    });
    
    // Set again after a short delay to catch any late changes
    setTimeout(() => {
      summaryElement.style.setProperty('display', 'block', 'important');
      summaryElement.style.setProperty('visibility', 'visible', 'important');
      summaryElement.style.setProperty('opacity', '1', 'important');
    }, 10);
    
    // Add a MutationObserver to watch for any changes that might hide the summary
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          // When details opens, ensure summary is visible
          if (detailsElement.hasAttribute('open')) {
            summaryElement.style.setProperty('display', 'block', 'important');
            summaryElement.style.setProperty('visibility', 'visible', 'important');
            summaryElement.style.setProperty('opacity', '1', 'important');
          }
        }
      });
    });
    
    // Observe the details element for attribute changes
    observer.observe(detailsElement, {
      attributes: true,
      attributeFilter: ['open']
    });
    
    // Store observer reference so we can disconnect it later if needed
    detailsElement._summaryObserver = observer;
    
    // Add menu-opening class immediately
    detailsElement.classList.add('menu-opening');
    summaryElement.setAttribute('aria-expanded', 'true');
    
    if (parentMenuElement) {
      parentMenuElement.classList.add('submenu-open');
      
      // CRITICAL: Add class to the li containing the open details so CSS can target it
      const listItem = detailsElement.closest('li');
      if (listItem) {
        listItem.classList.add('has-open-details');
        // Set inline styles IMMEDIATELY to ensure visibility
        listItem.style.setProperty('visibility', 'visible', 'important');
        listItem.style.setProperty('opacity', '1', 'important');
        listItem.style.setProperty('display', 'list-item', 'important');
      }
      
      // CRITICAL: Ensure details and summary are visible IMMEDIATELY
      detailsElement.style.setProperty('visibility', 'visible', 'important');
      detailsElement.style.setProperty('opacity', '1', 'important');
      detailsElement.style.setProperty('display', 'block', 'important');
      
      summaryElement.style.setProperty('visibility', 'visible', 'important');
      summaryElement.style.setProperty('display', 'block', 'important');
      summaryElement.style.setProperty('opacity', '1', 'important');
      summaryElement.style.setProperty('position', 'relative', 'important');
      summaryElement.style.setProperty('z-index', '11', 'important');
      
      // Also set in requestAnimationFrame as backup
      requestAnimationFrame(() => {
        if (listItem) {
          listItem.style.setProperty('visibility', 'visible', 'important');
          listItem.style.setProperty('opacity', '1', 'important');
        }
        detailsElement.style.setProperty('visibility', 'visible', 'important');
        detailsElement.style.setProperty('opacity', '1', 'important');
        summaryElement.style.setProperty('visibility', 'visible', 'important');
        summaryElement.style.setProperty('display', 'block', 'important');
        summaryElement.style.setProperty('opacity', '1', 'important');
      });
    }
    
    // Force the submenu to be visible immediately with inline styles
    // Set styles individually to ensure they apply with !important
    console.log('🎯 Setting submenu styles...');
    submenu.style.setProperty('display', 'block', 'important');
    submenu.style.setProperty('visibility', 'visible', 'important');
    submenu.style.setProperty('opacity', '1', 'important');
    submenu.style.setProperty('pointer-events', 'auto', 'important');
    submenu.style.setProperty('transform', 'translateX(0)', 'important');
    
    // Verify styles were applied
    setTimeout(() => {
      const computed = window.getComputedStyle(submenu);
      console.log('🎯 Submenu computed styles after setting:');
      console.log('  - transform:', computed.transform);
      console.log('  - visibility:', computed.visibility);
      console.log('  - opacity:', computed.opacity);
      console.log('  - display:', computed.display);
    }, 50);
    submenu.style.setProperty('position', 'absolute', 'important');
    submenu.style.setProperty('top', '0', 'important');
    submenu.style.setProperty('left', '0', 'important');
    submenu.style.setProperty('right', '0', 'important');
    submenu.style.setProperty('bottom', '0', 'important');
    submenu.style.setProperty('width', '100%', 'important');
    submenu.style.setProperty('height', '100%', 'important');
    submenu.style.setProperty('z-index', '10', 'important');
    submenu.style.setProperty('background-color', '#ffffff', 'important');
    submenu.style.setProperty('overflow-y', 'auto', 'important');
    submenu.style.setProperty('overflow-x', 'hidden', 'important');
    submenu.style.setProperty('transition', 'transform 0.5s ease, visibility 0.5s ease, opacity 0.5s ease', 'important');
    
    // Use setTimeout only for focus trap, not for opening
    const addTrapFocus = () => {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    };
    
    setTimeout(() => {
      !reducedMotion || reducedMotion.matches
        ? addTrapFocus()
        : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
    }, 100);
    
    // Return false to ensure nothing else happens
    return false;
  }

  openMenuDrawer(summaryElement) {
    // Explicitly set the open attribute
    this.mainDetailsToggle.setAttribute('open', '');
    
    // Remove any inline display styles from drawer that might block it
    const drawer = this.querySelector('.menu-drawer');
    if (drawer) {
      drawer.style.removeProperty('display');
    }
    
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    // Allow closing even without event (for programmatic closes)
    if (event === undefined && !this.mainDetailsToggle.hasAttribute('open')) return;

    // CRITICAL: Don't close if we're preventing main drawer close (submenu back button was clicked)
    if (document._preventingMainDrawerClose) {
      console.log('🚨 MenuDrawer.closeMenuDrawer: Prevented from closing because _preventingMainDrawerClose is true');
      return;
    }

    // Remove menu-opening class to trigger CSS close animation
    this.mainDetailsToggle.classList.remove('menu-opening');
    
    // Wait for CSS transition to complete (500ms) before removing open attribute
    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute('open');
      
      // Close all submenus and reset their state
      // Search in both the main toggle and the drawer container (in case submenus were moved)
      const drawer = this.querySelector('.menu-drawer');
      const drawerInner = drawer?.querySelector('.menu-drawer__inner-container');
      
      // Find all details elements in the navigation
      const allDetails = this.mainDetailsToggle.querySelectorAll('details');
      allDetails.forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        details.classList.remove('has-open-submenu');
        
        // Reset summary aria
        const summary = details.querySelector('summary.menu-drawer__menu-item');
        if (summary) {
          summary.setAttribute('aria-expanded', 'false');
        }
      });
      
      // Find all submenus (including those that might have been moved to drawer container)
      const allSubmenus = [];
      if (drawer) {
        // Search in navigation
        const navSubmenus = drawer.querySelectorAll('.menu-drawer__submenu');
        navSubmenus.forEach(submenu => allSubmenus.push(submenu));
        
        // Search in drawer inner container (where moved submenus might be)
        if (drawerInner) {
          const movedSubmenus = drawerInner.querySelectorAll('.menu-drawer__submenu');
          movedSubmenus.forEach(submenu => {
            if (!allSubmenus.includes(submenu)) {
              allSubmenus.push(submenu);
            }
          });
        }
      }
      
      // Reset all submenus
      allSubmenus.forEach((submenu) => {
        submenu.classList.remove('submenu-is-open');
        
        // Restore submenu to original parent if it was moved
        if (submenu._originalParent && submenu.parentElement !== submenu._originalParent) {
          submenu._originalParent.appendChild(submenu);
          delete submenu._originalParent;
          delete submenu._originalDetails;
        }
      });
      
      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
      // Also remove the has-open-details class from list items
      this.mainDetailsToggle.querySelectorAll('li.has-open-details').forEach((li) => {
        li.classList.remove('has-open-details');
      });
    }, 500);
    
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);

    if (event instanceof KeyboardEvent && elementToFocus) {
      elementToFocus.setAttribute('aria-expanded', false);
    }
    if (elementToFocus && !(event instanceof KeyboardEvent)) {
      elementToFocus.setAttribute('aria-expanded', false);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Don't close if we're preventing main drawer close (submenu back button was clicked)
      if (document._preventingMainDrawerClose) {
        console.log('🎯 onFocusOut: Ignoring because _preventingMainDrawerClose is true');
        return;
      }
      
      // Don't close if we just closed a submenu (back button was clicked)
      if (document._justClosedSubmenu) {
        console.log('🎯 onFocusOut: Ignoring because submenu was just closed');
        return;
      }
      
      // CRITICAL: Don't close if clicking inside the drawer
      const drawer = this.querySelector('.menu-drawer');
      if (drawer && drawer.contains(document.activeElement)) {
        console.log('🎯 onFocusOut: Active element is inside drawer, not closing');
        return;
      }
      
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
        console.log('🎯 onFocusOut: Closing drawer because focus moved outside');
        this.closeMenuDrawer();
      }
    }, 100); // Add small delay to allow focus to settle
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement && detailsElement !== this.mainDetailsToggle) {
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    
    // Remove the has-open-details class from the list item
    const listItem = detailsElement.closest('li');
    if (listItem) {
      listItem.classList.remove('has-open-details');
    }
    
    detailsElement.classList.remove('menu-opening');
    const summary = detailsElement.querySelector('summary');
    summary.setAttribute('aria-expanded', false);
    // Ensure summary stays visible when closing
    summary.style.display = '';
    summary.style.visibility = '';
    removeTrapFocus(summary);
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }
  
  connectedCallback() {
    super.connectedCallback();
  }

  bindEvents() {
    super.bindEvents();
    
    // Add close button handler for header drawer
    // Use event delegation on the drawer container to catch clicks even if button is added later
    const drawer = this.querySelector('.menu-drawer');
    if (drawer) {
      drawer.addEventListener('click', (event) => {
        const closeButton = event.target.closest('.menu-drawer__close-button-top');
    if (closeButton) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          const summary = this.mainDetailsToggle.querySelector('summary');
          if (this.mainDetailsToggle.hasAttribute('open')) {
            this.closeMenuDrawer(event, summary);
          }
          return false;
        }
      }, true); // Use capture phase to ensure it fires first
    }
    
    // Also attach directly to the button if it exists
    const setupCloseButton = () => {
      const closeButton = this.querySelector('.menu-drawer__close-button-top');
      if (closeButton && !closeButton.hasAttribute('data-close-handler-attached')) {
        closeButton.setAttribute('data-close-handler-attached', 'true');
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
          event.stopImmediatePropagation();
        const summary = this.mainDetailsToggle.querySelector('summary');
        if (this.mainDetailsToggle.hasAttribute('open')) {
          this.closeMenuDrawer(event, summary);
        }
          return false;
        }, true); // Use capture phase
      }
    };
    
    // Setup immediately
    setupCloseButton();
    
    // Also setup after a short delay in case drawer isn't rendered yet
    setTimeout(setupCloseButton, 100);
    setTimeout(setupCloseButton, 500);
    
    // Add click-outside-to-close functionality
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }
  
  handleDocumentClick(event) {
    if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) {
      return;
    }
    
    // CRITICAL: Check for submenu back button FIRST (before any other checks)
    if (event._isSubmenuBackButton) {
      console.log('🎯 handleDocumentClick: Ignoring submenu back button click');
      return;
    }
    
    // Also check if clicking on submenu back button directly
    const backButton = event.target.closest('.menu-drawer__close-button');
    if (backButton && !backButton.classList.contains('menu-drawer__close-button-top')) {
      console.log('🎯 handleDocumentClick: Ignoring submenu back button (direct check)');
      return;
    }
    
    // CRITICAL: If clicking on the close button (X), let it handle the close
    const closeButton = event.target.closest('.menu-drawer__close-button-top');
    if (closeButton) {
      // Don't interfere - let the close button handler do its job
      return;
    }
    
    // CRITICAL: If this is a menu item click, ignore it completely
    if (event._isMenuItemClick) {
      return;
    }
    
    const drawer = this.querySelector('.menu-drawer');
    const summary = this.mainDetailsToggle.querySelector('summary');
    
    // CRITICAL: Check if clicking INSIDE the drawer FIRST
    // This prevents the drawer from closing when clicking SHOP to open submenu
    if (drawer && drawer.contains(event.target)) {
      // Check if clicking on a menu item summary or its details element
      const clickedSummary = event.target.closest('summary.menu-drawer__menu-item');
      const clickedDetails = event.target.closest('details[id^="Details-menu-drawer-menu-item"]');
      
      if (clickedSummary || clickedDetails) {
        // Don't close - let the menu item handler deal with it
        event.stopPropagation();
        return;
      }
      
      // Check if clicking on any summary inside drawer (except main toggle)
      const anySummary = event.target.closest('summary');
      if (anySummary && anySummary !== summary) {
        // It's a menu item summary - don't close
        event.stopPropagation();
        return;
      }
      
      // Check if clicking on submenu or its contents (including back button)
      const clickedSubmenu = event.target.closest('.menu-drawer__submenu');
      if (clickedSubmenu) {
        // Also check if it's the back button
        const backButton = event.target.closest('.menu-drawer__close-button');
        if (backButton && !backButton.classList.contains('menu-drawer__close-button-top')) {
          // This is the submenu back button - don't close main drawer
          event.stopPropagation();
          return;
        }
        event.stopPropagation();
        return;
      }
      
      // If clicking anywhere else inside drawer, don't close
      event.stopPropagation();
      return;
    }
    
    // Also check if clicking on a details element inside the drawer
    const clickedDetails = event.target.closest('details');
    if (clickedDetails && clickedDetails !== this.mainDetailsToggle) {
      if (drawer && drawer.contains(clickedDetails)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    }
    
    // Don't close if clicking inside the drawer (including submenus)
    if (drawer && drawer.contains(event.target)) {
      return;
    }
    
    // Don't close if clicking on any menu item or button inside the drawer
    const clickedMenuItem = event.target.closest('.menu-drawer__menu-item');
    const clickedSubmenu = event.target.closest('.menu-drawer__submenu');
    const clickedCloseButton = event.target.closest('.menu-drawer__close-button');
    
    // If clicking on any menu item inside the drawer, don't close
    if (clickedMenuItem && drawer && drawer.contains(clickedMenuItem)) {
      return;
    }
    
    // If clicking on submenu or its contents, don't close
    if (clickedSubmenu && drawer && drawer.contains(clickedSubmenu)) {
      return;
    }
    
    // If clicking on close button (submenu back button), don't close main drawer
    if (clickedCloseButton && clickedCloseButton.classList.contains('menu-drawer__close-button') && 
        !clickedCloseButton.classList.contains('menu-drawer__close-button-top')) {
      return;
    }
    
    // If clicking on any details inside the drawer, don't close
    if (clickedDetails && drawer && drawer.contains(clickedDetails)) {
      return;
    }
    
    // Close if clicking outside
    if (!this.contains(event.target)) {
      event.stopPropagation();
      this.closeMenuDrawer(event, summary);
    }
  }

  openMenuDrawer(summaryElement) {
    // Explicitly set the open attribute FIRST
    this.mainDetailsToggle.setAttribute('open', '');
    
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    // Force white background on drawer and transparent on containers
    const drawer = this.querySelector('.menu-drawer');
    const container = this.querySelector('.menu-drawer-container');
    const details = this.mainDetailsToggle;
    
    // Make containers transparent
    if (container) {
      container.style.setProperty('background', 'transparent', 'important');
      container.style.setProperty('background-color', 'transparent', 'important');
    }
    if (details) {
      details.style.setProperty('background', 'transparent', 'important');
      details.style.setProperty('background-color', 'transparent', 'important');
    }
    this.style.setProperty('background', 'transparent', 'important');
    this.style.setProperty('background-color', 'transparent', 'important');
    
    // Make drawer white and ensure it's visible
    if (drawer) {
      // Remove any inline display styles that might have been set when closing
      drawer.style.removeProperty('display');
      drawer.style.removeProperty('transform');
      drawer.style.removeProperty('visibility');
      drawer.style.removeProperty('opacity');
      drawer.style.setProperty('background', '#ffffff', 'important');
      drawer.style.setProperty('background-color', '#ffffff', 'important');
      drawer.style.setProperty('background-image', 'none', 'important');
      drawer.style.setProperty('--gradient-background', '#ffffff', 'important');
    }

    // Add menu-opening class immediately to trigger CSS animation
    // Use setTimeout(0) to ensure the open attribute is set first, then add menu-opening for smooth animation
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    }, 0);

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    
    // Add document click listener for click-outside-to-close (use capture phase but lower priority)
    // Use setTimeout to ensure summary clicks are handled first
    setTimeout(() => {
      document.addEventListener('click', this.handleDocumentClick, true);
    }, 0);
  }

  closeMenuDrawer(event, elementToFocus) {
    // CRITICAL: Don't close if we're preventing main drawer close (submenu back button was clicked)
    if (document._preventingMainDrawerClose) {
      console.log('🚨 HeaderDrawer.closeMenuDrawer: Prevented from closing because _preventingMainDrawerClose is true');
      return;
    }
    
    // Remove document click listener IMMEDIATELY
    document.removeEventListener('click', this.handleDocumentClick, true);
    
    // Remove menu-opening class IMMEDIATELY to start close animation
    // Don't manipulate transform/visibility/opacity - let CSS handle the animation
    this.mainDetailsToggle.classList.remove('menu-opening');
    
    // Remove any inline styles that might interfere with CSS transitions
    const drawer = this.querySelector('.menu-drawer');
    if (drawer) {
      drawer.style.removeProperty('transform');
      drawer.style.removeProperty('visibility');
      drawer.style.removeProperty('opacity');
    }
    
    const summary = elementToFocus || this.mainDetailsToggle.querySelector('summary');
    
    // Call parent closeMenuDrawer which will handle the animation timing and remove 'open' attribute
    super.closeMenuDrawer(event, summary);
    
    if (this.header) {
      this.header.classList.remove('menu-open');
    }
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
